// The SSE-driven table state — DESIGN.md section 9.2: "single source of truth; no optimistic
// local state." Every screen that needs to know about the live match reads `tableStore.state`;
// writes go through the REST endpoints in src/backend.ts and this store's `state` updates only
// when the SSE stream echoes the change back.
import { reactive } from 'vue'
import { subscribeTableEvents } from '../backend'
import type { TableState } from '../types'

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

interface TableStoreState {
  table: string
  state: TableState | null
  status: ConnectionStatus
  everConnected: boolean
}

const params = new URLSearchParams(location.search)

export const tableStore = reactive<TableStoreState>({
  table: params.get('table') || 'main',
  state: null,
  status: 'connecting',
  everConnected: false,
})

// Reserved per DESIGN.md section 9.3 ("hides the browser-ish chrome (nothing today, reserved)")
// — parsed so the param round-trips, no behaviour hooked up yet.
export const kioskChrome = params.get('kiosk') !== '1'

let handle: { close: () => void } | null = null
const listeners = new Set<(state: TableState) => void>()

export function onTableState(fn: (state: TableState) => void): () => void {
  listeners.add(fn)
  if (tableStore.state) fn(tableStore.state)
  return () => listeners.delete(fn)
}

export function startTableStream() {
  if (handle) return
  handle = subscribeTableEvents(
    tableStore.table,
    (state) => {
      tableStore.state = state
      tableStore.status = 'connected'
      tableStore.everConnected = true
      listeners.forEach((fn) => fn(state))
    },
    (connected) => {
      tableStore.status = connected ? 'connected' : 'disconnected'
    },
  )
}
